// const fmsGlobal = getFmsGlobal();

import {JobNames} from "../../../../../framework.core/JobNames";
import React, {MutableRefObject, useContext, useEffect, useState} from "react";
import ApplicationContext, { IApplicationContext } from "../../../../../ApplicationContext";
import {useTypedFastStorage} from "../../../../../hooks/useFastStorage";
import _ from "lodash";
import TextBox from "../../../../../ui/TextBox";
import {ApplicableTableSettings} from "../../../../../tableSettings";
import {batchCheckProcessingCheckTable, batchCheckProcessingVendorTable} from "../../../../reports/tableSettings";
import {
  DateRenderer,
  FloatRenderer,
  TextAlignLeftRenderer,
} from "../../../../../uiControls/SmartTableRenderers";
import InlineInputControlRow from "../../../../../ui/layout/InlineInputControlRow";
import SecondaryButton from "../../../../../ui/SecondaryButton";
import Date from "../../../../../ui/Date";
import BankDropDown from "../../../../../uiControls/BankDropDown";
import PrimaryButton from "../../../../../ui/PrimaryButton";
import Float from "../../../../../ui/Float";
import {BatchCheckProcessingModel, VendorDetail} from "../../../../../fms.core/BatchCheckProcessingModel";
import CheckBox from "../../../../../ui/CheckBox";
import SmartTable from "../../../../../uiControls/SmartTable";
import {compareIso, getDate, now} from "../../../../../dateUtils";
import Label from "../../../../../ui/Label";
import useGlobalOkDialog from "../../../../../hooks/useGlobalOkDialog";
import { DocumentType } from "../../../../../framework.core/DocumentType";
import CheckPrint from "../../../../reports/CheckPrint";
import IfmsModal from "../../../../../ui/IfmsModal";

interface BatchCheckProcessingStorageKeys {
  modelStorage: string,
};

interface BatchCheckProcessingViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  jobName: JobNames,
  reload: () => void,
};

interface BatchCheckProcessingLoadedDetailsViewParams {
  batchCheckProcessing: BatchCheckProcessingModel,
  batchCheckProcessingRef: MutableRefObject<BatchCheckProcessingModel | null>,
  setBatchCheckProcessing: (x: Partial<BatchCheckProcessingModel>) => void,
  onPopupClose?: () => void,
  jobName: JobNames,
  tbId: string | undefined,
  appContext: IApplicationContext,
  reload: () => void,
};

function getBatchCheckProcessingStorageKeys(jobName: JobNames): BatchCheckProcessingStorageKeys {
  return {modelStorage: `bill-payment-bank-edi-${jobName}`}
}

const newBatchCheckProcessingModel = new BatchCheckProcessingModel();

function BatchCheckProcessingImpl(params: BatchCheckProcessingViewParams) {
  const {jobName, id} = params;
  const {modelStorage} = getBatchCheckProcessingStorageKeys(jobName);
  const [inStorage, setInStorage, removeBatchCheckProcessingFromStorage, batchCheckProcessingRef] = useTypedFastStorage<BatchCheckProcessingModel>(modelStorage, newBatchCheckProcessingModel);
  const appContext = useContext(ApplicationContext);

  useEffect(() => {
      initialize();
      return () => removeBatchCheckProcessingFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    const model = _.cloneDeep(newBatchCheckProcessingModel);
    model.accrualDate = now();
    model.expectDateTo = model.accrualDate;
    setInStorage(model);
  }

  async function initialize() {
    await loadExisting();
  }

  return (
    <BatchCheckProcessingInitializedImpl
      batchCheckProcessing={inStorage!}
      batchCheckProcessingRef={batchCheckProcessingRef}
      setBatchCheckProcessing={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
      tbId={id}
      appContext={appContext}
      reload={params.reload}
    />
  );
}

function IndicatorRenderer({row}: any) {
  return <div style={{paddingTop: '4px', width: '100%'}}>{row.index + 1}</div>;
}

function SelectRenderer({data, row}: any) {
  const toggleSelection = data[row.index].toggleSelection;
  const isView = data[row.index].isView;

  return <div style={{width: '100%'}}><CheckBox value={data[row.index].select}
                                                onChange={() => toggleSelection(row.index)} disabled={isView}/></div>;
}

function BatchCheckProcessingInitializedImpl({
                                               batchCheckProcessing,
                                               batchCheckProcessingRef,
                                               setBatchCheckProcessing,
                                               onPopupClose,
                                               jobName,
                                               tbId,
                                               appContext,
                                               reload
                                             }: BatchCheckProcessingLoadedDetailsViewParams) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [original, setOriginal] = useState<BatchCheckProcessingModel>(newBatchCheckProcessingModel);
  const [showIssueCheckPopup, setShowIssueCheckPopup] = useState<boolean>(false);

  function onChange(change: Partial<BatchCheckProcessingModel>) {
    setBatchCheckProcessing(change);
  }

  function filterCheckData(vendorDetails: VendorDetail[]) {
    
    return original.checkDetails
       .filter(item => vendorDetails.find(it => item.payToOrderId === it.payToId && it.select))
       .map((item, index) => ({
         ...item,
         checkNo: (parseInt(batchCheckProcessing.checkNo||'0') +  + index),
         amount: vendorDetails.filter(it => it.select && it.payToId === item.payToOrderId)
           .reduce((sum, it) => sum + it.amount, 0),  
         billIds: vendorDetails.filter(it => it.select && it.payToId === item.payToOrderId).reduce((id, it) => {
            if(!id) return id + it.id;
            else return id + `, ${it.id}`;
         } ,'')
       }));
  }

  function toggleSelection(i: number) {
    const _list = _.cloneDeep(batchCheckProcessing.vendorDetails);
    _list[i].select = !_list[i].select;
    onChange({vendorDetails: _list, checkDetails: filterCheckData(_list)})
    //console.log(batchCheckProcessing.checkDetails);
  }

  const totalAmount = batchCheckProcessing.checkDetails.reduce((sum, item) => sum + item.amount, 0)

  function TotalLabelRenderer() {
    return <div className="ifms-input-ctr-row2-label"
                style={{textAlign: 'right', marginRight: '5px', marginTop: '10px'}}>
      Total
    </div>;
  }

  function TotalAmountRenderer() {
    return <div className='mt-2 ' style={{lineHeight: '15px'}}><Float decimals={2} disabled value={totalAmount}/></div>;
  }

  const vendorTableOptions: ApplicableTableSettings = {
    tableName: 'batchCheckProcessingVendorTable',
    ...batchCheckProcessingVendorTable,
    
    data: batchCheckProcessing?.vendorDetails.map((x, i) => ({...x, i, toggleSelection})),
    rowRenderers: [
      {accessor: 'select', Cell: SelectRenderer},
      {accessor: 'billNo', Cell: TextAlignLeftRenderer},
      {accessor: 'invoiceDate', Cell: DateRenderer},
      {accessor: 'amount', Cell: FloatRenderer},
      {accessor: 'description', Cell: TextAlignLeftRenderer},
      {accessor: 'vendor', Cell: TextAlignLeftRenderer},
      {accessor: 'checkSeq', Cell: TextAlignLeftRenderer},
    ],
  };

  const checkTableOptions: ApplicableTableSettings = {
    tableName: 'batchCheckProcessingCheckTable',
    ...batchCheckProcessingCheckTable,
    
    data: batchCheckProcessing?.checkDetails.map((x, i) => ({...x, i, toggleSelection})),
    rowRenderers: [
      {accessor: 'indicator', Cell: IndicatorRenderer},
      {accessor: 'payToOrderName', Cell: TextAlignLeftRenderer},
      {accessor: 'amount', Cell: FloatRenderer},
      {accessor: 'checkNo', Cell: TextAlignLeftRenderer},
    ],
    footerRow: [
      {
        colspan: 3,
        renderer: TotalLabelRenderer
      },
      {
        colspan: 1,
        renderer: TotalAmountRenderer
      },
    ],
  };

  function selectAllInTable() {
    const _list = _.cloneDeep(batchCheckProcessing.vendorDetails);
    _list.forEach(item => item.select = true);
    onChange({vendorDetails: _list, checkDetails: filterCheckData(_list)})
  }

  function clearTable() {
    const _list = _.cloneDeep(batchCheckProcessing.vendorDetails);
    _list.forEach(item => item.select = false);
    onChange({vendorDetails: _list, checkDetails: filterCheckData(_list)})
  }

  async function setNextCheckNo(){
    const checkNo = await appContext.api.getAutoCheckNo(batchCheckProcessing.bankId, appContext.user?.branch);
    setListCheckNo(checkNo)
  }

  function setListCheckNo(nextCheckNo: string){
    const _list = _.cloneDeep(batchCheckProcessing.checkDetails);
    _list.forEach((item, index) => {
      item.checkNo = parseInt(nextCheckNo||'0') + index;
    });

    onChange({checkDetails: _list, checkNo: nextCheckNo.toString()});
  }

  async function getList() {
    const result: BatchCheckProcessingModel = await appContext.api.loadApList(batchCheckProcessing.expectDateFrom || "", batchCheckProcessing.expectDateTo || "", batchCheckProcessing.checkNo || "")
    if (result) {
      onChange({vendorDetails: result.vendorDetails, checkDetails: result.checkDetails});
      setOriginal(_.cloneDeep(result));
    }
  }

  function IssueCheck(){
    return(
      <SecondaryButton
          size="regular"
          style={{width: '120px'}}
          onClick={async () => {
            await save();
          }}>
          ISSUE CHECK
      </SecondaryButton>
    );
  }

  function onCheckPopupClose(){
    setShowIssueCheckPopup(false);
    if(onPopupClose){
       reload();
       onPopupClose();
     }
  }

  async function save(){
    let errorMsg = '';
    //CheckData
    if(!batchCheckProcessing.checkNo || batchCheckProcessing.checkNo === '0'){
      errorMsg = 'Input [Check #]';
    }
    if(batchCheckProcessing.bankId === 0){
      errorMsg = errorMsg + (errorMsg.length ? "\r\n" : "") + 'Select [Bank]';
    }
    if(!batchCheckProcessing.accrualDate){
      errorMsg = errorMsg + (errorMsg.length ? "\r\n" : "") + 'Input [Accrual Date]';
    }else if(compareIso(getDate(batchCheckProcessing.accrualDate)??"", getDate(now())??"") > 0){
      errorMsg = errorMsg + (errorMsg.length ? "\r\n" : "") + 'Invalid Accrual Date, Accrual Date should not be later than today.';
    }

    if(errorMsg) {
      await showGlobalOkDialog(<p>{errorMsg}</p>, false, null, "info");
      return null;
    }

    const apiResult = await appContext.api.issueCheck(batchCheckProcessing);
    if(apiResult){

      if(!appContext.suntekSettings?.accounting?.doNotAskPrintCheckInNewBillPayment){
          setShowIssueCheckPopup(true);
      } else {
        if(onPopupClose){
          reload();
          onPopupClose();
        }
      }
    }else{
      await showGlobalOkDialog(<p>{'Save Failed'}</p>, false, null, "error");
    }
  }

  return (
    <div className='report-popup'>
      <div style={{display: 'flex'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow label="Schedule Date" mode='smaller-2x' smallMargin>
            <div style={{display: 'flex'}}>
              <div style={{width: '85px'}}>
                <Date hideIcon value={batchCheckProcessing.expectDateFrom} onChange={expectDateFrom => onChange({expectDateFrom})} />
              </div>

              <div style={{width: '40px', textAlign: 'center'}}>
                ~
              </div>

              <div style={{width: '85px'}}>
                <Date hideIcon value={batchCheckProcessing.expectDateTo} onChange={expectDateTo => {
                  if (expectDateTo && batchCheckProcessing.accrualDate && compareIso(getDate(expectDateTo)??"", getDate(batchCheckProcessing.accrualDate)??"") > 0) {
                    showGlobalOkDialog('Invalid Date, date should not be later than Accrual Date.')
                    onChange({
                      expectDateTo: batchCheckProcessing.accrualDate
                    })
                    return;
                  }

                  onChange({expectDateTo});
                }} />
              </div>
            </div>
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '320px'}}>
          <InlineInputControlRow label="Accrual Date" mode='smaller-2x' smallMargin>
            <div style={{width: '85px'}}>
              <Date hideIcon value={batchCheckProcessing.accrualDate} onChange={accrualDate => onChange({
                accrualDate,
                expectDateTo: accrualDate,
              })} />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow label="Start Check #" mode='smaller-2x' smallMargin onLabelClick={setNextCheckNo}>
            <div style={{width: '85px'}}>
              <TextBox value={batchCheckProcessing.checkNo} onChange={e => setListCheckNo(e.target.value)} />
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow label="Bank" mode='smaller-2x' smallMargin>
            <BankDropDown value={batchCheckProcessing.bankId} onChange={bank => onChange({bankId: bank?.id || 0, bankName: bank?.bankName})} autoSetDefault />
          </InlineInputControlRow>
        </div>
      </div>

      <InlineInputControlRow smallMargin>&nbsp;</InlineInputControlRow>
      <div className='clearfix'/>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end'
      }}>
        <Label style={{marginLeft: 60, position: 'relative', top: '5px'}} label='Approved Bill(s) to Pay' bold/>
        <div style={{
          display: "flex",
          float: 'right',
          justifyContent: 'space-between',
          width: 160,
          paddingBottom: 5,
          marginTop: 10
        }}>
          <PrimaryButton size='extrasmall' onClick={getList}>Load</PrimaryButton>
          <PrimaryButton size='extrasmall' onClick={selectAllInTable}>ALL</PrimaryButton>
          <PrimaryButton size='extrasmall' onClick={clearTable}>CLEAR</PrimaryButton>
        </div>
      </div>
      <div className='report-row mt-1'>
        <SmartTable
          {...vendorTableOptions}
        />
      </div>
      <div style={{marginTop: '5px'}}>
        <Label style={{marginLeft: 30, marginBottom: '0px'}} label='Check(s) to Issue' bold />
      </div>
      <div className='report-row mt-1' style={{width: 390}}>
        <SmartTable
          {...checkTableOptions}
        />
      </div>
      <div className='clearfix'/>
      <div className='report-footer'>
        {IssueCheck()}
        <SecondaryButton
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
      <IfmsModal isBlueHeader cancelOnEsc onCancel={onCheckPopupClose} draggable isOpen={showIssueCheckPopup} title="Print Check" width="450px">
        {showIssueCheckPopup &&
          <CheckPrint onPopupClose={onCheckPopupClose} checkRange={true} selectedElement={{
            id: parseInt(tbId??'0'),
            bankId: batchCheckProcessing.bankId, 
            checkFrom: batchCheckProcessing.checkDetails[0]?.checkNo.toString(), 
            checkTo : batchCheckProcessing.checkDetails[batchCheckProcessing.checkDetails.length-1]?.checkNo.toString()}}  
            documentType={DocumentType.CheckS} batchCheck={true} />
        }
      </IfmsModal>
    </div>
  );
}

const BatchCheckProcessingDetails = ({
                                       onPopupClose,
                                       jobName,
                                       selectedElementId,
                                       reload
                                     }: { onPopupClose?: () => void, jobName: JobNames, selectedElementId: number, reload: () => void}) => {
  if (!selectedElementId) return null;

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <BatchCheckProcessingImpl id={selectedElementId.toString()} jobName={jobName} onPopupClose={onPopupClose} reload={reload}
                              key={`batchCheckProcessing-details`} />
  );
};
export default BatchCheckProcessingDetails;
